import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, patch, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

export type SignalTier = "critical" | "warning" | "opp" | "maint";

/** Where a guide step's link goes (a Target Registry target). */
export interface SignalGuideLink {
  target: string;
  label: string;
  kind: "assess" | "relationships" | "synopsis" | "path" | "brief";
  path: string | null;
}

/** An Action Guide as the backend delivers it (Signal Administration →
 * Action Guides). `why` may hold {{field}} placeholders, filled from the
 * firing's triggered fields like the card copy. */
export interface SignalGuide {
  title: string;
  why: string;
  clears_when: string;
  steps: {
    title: string;
    body: string;
    link: SignalGuideLink | null;
    /** Present on action-plan-derived guides (who owns the step). */
    owner?: string | null;
    owner_label?: string | null;
  }[];
}

export interface SignalCard {
  signal_firing_id: string;
  account_id: string;
  account_name: string;
  signal_key: string;
  tier: SignalTier;
  state: string;
  first_fired_at: string;
  persona: string | null;
  evidence: {
    triggeredFields?: Record<string, unknown>;
    conditionString?: string;
    ruleVersion?: string;
    secondarySignals?: {
      key: string;
      tier: SignalTier;
      title: string;
      conditionString: string;
    }[];
  } | null;
  /** Effective CTA from the backend: an Izba-admin override (Signal
   * Administration → CTA Configuration) or ExecBridge's default. Absent
   * from older API responses — cards fall back to signalCTAs.ts. */
  cta?: {
    label: string;
    kind: "assess" | "relationships" | "synopsis" | "path" | "brief";
    /** In-app path for an admin-authored Target Registry target. */
    path?: string | null;
    /** A published Action Guide: the CTA opens it instead of navigating. */
    guide?: SignalGuide | null;
    /** Published card text (title / message / why); absent fields keep
     * the default text in signalCTAs.ts. */
    card_copy?: { title?: string; text?: string; why?: string } | null;
    source: "override" | "default" | "action_plan";
    /** Set when an Izba admin approved an action plan for this signal: the
     * button is its one action, or opens its steps. */
    plan?: {
      bucket:
        | "single_independent"
        | "single_dependent"
        | "multiple_independent"
        | "multiple_dependent";
      awareness: boolean;
      steps: number;
    } | null;
    /** Awareness plans: acting doesn't clear the card, data changes do. */
    awareness_note?: string | null;
    /** A single-action plan's guidance line, shown above the button. */
    detail?: string | null;
  } | null;
  /** Titles of the guide steps this user has ticked (per firing). */
  guide_progress?: string[];
  /** One card per account (2026-10-23): the account's other open signals,
   * highest tier first then catalogue order — the "+N more signals"
   * disclosure. Mark as handled / Snooze act on the primary only; then the
   * first of these becomes the primary. */
  secondary?: SignalCard[];
  /** What the rules read at the account's latest check (S-03): the card
   * header shows these so it agrees with why the card fired. Absent until
   * the account has been checked once. */
  evaluated_context?: SignalEvaluatedContext | null;
}

export interface SignalEvaluatedContext {
  al: number | null;
  rl: number | null;
  al_stale_days: number | null;
  renewal_days: number | null;
  cluster: string | null;
  ah: number | null;
  bh: number | null;
  bh_class: "strong" | "stable" | "concern" | "no_data" | null;
  rl_conditions: string | null;
  target_al_committed: number | null;
  evaluated_at: string | null;
}

/** After the primary leaves (handled or snoozed), the next signal in the
 * account's queue becomes the card; with none left the card goes. */
const advanceQueue = (cards: SignalCard[], firingId: string): SignalCard[] =>
  cards.flatMap(card => {
    if (card.signal_firing_id !== firingId) return [card];
    const [next, ...rest] = card.secondary ?? [];
    return next ? [{ ...next, secondary: rest }] : [];
  });

/**
 * What this org's users may see of signal output, set by Izba admins in
 * Signal Administration (backend services/signal_admin_config_service.py).
 * The backend already blanks signal data when `signals_visible` is false;
 * this drives the chrome — Actions tab disabled/hidden, policy notices.
 */
export interface SignalPresentation {
  service_mode: "active" | "inactive";
  actions_surface: "enabled" | "disabled" | "hidden";
  effective_actions_surface: "enabled" | "disabled" | "hidden";
  signals_visible: boolean;
  history_available: boolean;
  config_version: number;
  /** Admin-published card titles by signal_key ("key:variant" for
   * rl-expansion's variants), for pages that name a signal without a card. */
  signal_titles?: Record<string, string>;
}

const FALLBACK_PRESENTATION: SignalPresentation = {
  service_mode: "active",
  actions_surface: "enabled",
  effective_actions_surface: "enabled",
  signals_visible: true,
  history_available: true,
  config_version: 0,
};

interface SignalFeedState {
  cards: SignalCard[];
  /** null until the first feed response arrives. */
  presentation: SignalPresentation | null;
  /** Signal parameter values in force for this org ({parameter-id: value}),
   * filling {{parameter-id}} placeholders in card, guide and plan text so
   * it states the thresholds actually applied. */
  parameters: Record<string, number | string>;
  loading: boolean;
  error: string | null;
  actionInFlightId: string | null;
}

const initialState: SignalFeedState = {
  cards: [],
  presentation: null,
  parameters: {},
  loading: false,
  error: null,
  actionInFlightId: null,
};

export const fetchSignalFeed = createAsyncThunk(
  "signalFeed/fetch",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getSignalFeed());
      const content = (response as any)?.content || response;
      return {
        cards: (content?.cards || []) as SignalCard[],
        presentation: (content?.presentation ??
          null) as SignalPresentation | null,
        parameters: (content?.parameters ?? {}) as Record<
          string,
          number | string
        >,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch signal feed"
      );
    }
  }
);

/** Presentation alone (incl. published titles), for pages that name
 * signals but don't load the feed — e.g. Signal History. */
export const fetchSignalPresentation = createAsyncThunk(
  "signalFeed/fetchPresentation",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getSignalPresentation());
      const content = (response as any)?.content || response;
      return (content?.presentation ?? null) as SignalPresentation | null;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch signal presentation"
      );
    }
  }
);

/** §9.3 attribution: log a card's CTA click. Fire-and-forget — a failed log
 * must never block the navigation the user asked for. */
export const recordSignalCtaClick = createAsyncThunk(
  "signalFeed/ctaClick",
  async ({ firingId, label }: { firingId: string; label: string }) => {
    try {
      await post(API_URLS.recordSignalCtaClick(firingId), { cta_label: label });
    } catch {
      // Attribution is best effort.
    }
  }
);

/** Tick or untick an Action Guide step for this user. Updated on screen
 * straight away; the server keeps it across sessions. */
export const toggleGuideStep = createAsyncThunk(
  "signalFeed/guideStep",
  async (
    {
      firingId,
      step,
      title,
      done,
    }: { firingId: string; step: number; title: string; done: boolean },
    { rejectWithValue }
  ) => {
    try {
      await post(API_URLS.recordSignalGuideStep(firingId), {
        step,
        title,
        done,
      });
      return { firingId, title, done };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Couldn't save that step");
    }
  }
);

export const snoozeSignal = createAsyncThunk(
  "signalFeed/snooze",
  async (
    { firingId, days = 7 }: { firingId: string; days?: number },
    { rejectWithValue }
  ) => {
    try {
      await patch(API_URLS.snoozeSignalFiring(firingId), { days });
      return firingId;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to snooze signal"
      );
    }
  }
);

export const dismissSignal = createAsyncThunk(
  "signalFeed/dismiss",
  async (firingId: string, { rejectWithValue }) => {
    try {
      await patch(API_URLS.dismissSignalFiring(firingId), {});
      return firingId;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to dismiss signal"
      );
    }
  }
);

const signalFeedSlice = createSlice({
  name: "signalFeed",
  initialState,
  reducers: {
    clearSignalFeed: state => {
      state.cards = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSignalFeed.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSignalFeed.fulfilled, (state, action) => {
        state.loading = false;
        state.cards = action.payload.cards;
        // A response without a policy (S-05) keeps the one we have, else
        // reads as enabled — the backend already blanks card data when
        // signals are off — rather than leaving the Actions tab loading.
        state.presentation =
          action.payload.presentation ??
          state.presentation ??
          FALLBACK_PRESENTATION;
        state.parameters = action.payload.parameters;
        state.error = null;
      })
      .addCase(toggleGuideStep.pending, (state, action) => {
        const { firingId, title, done } = action.meta.arg;
        const card = state.cards.find(c => c.signal_firing_id === firingId);
        if (!card) return;
        const ticked = new Set(card.guide_progress ?? []);
        if (done) ticked.add(title);
        else ticked.delete(title);
        card.guide_progress = Array.from(ticked);
      })
      .addCase(toggleGuideStep.rejected, (state, action) => {
        // Undo the optimistic tick.
        const { firingId, title, done } = action.meta.arg;
        const card = state.cards.find(c => c.signal_firing_id === firingId);
        if (!card) return;
        const ticked = new Set(card.guide_progress ?? []);
        if (done) ticked.delete(title);
        else ticked.add(title);
        card.guide_progress = Array.from(ticked);
      })
      .addCase(fetchSignalPresentation.fulfilled, (state, action) => {
        if (action.payload) state.presentation = action.payload;
      })
      .addCase(fetchSignalFeed.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || "Unknown error";
      })
      .addCase(snoozeSignal.pending, (state, action) => {
        state.actionInFlightId = action.meta.arg.firingId;
      })
      .addCase(snoozeSignal.fulfilled, (state, action) => {
        state.actionInFlightId = null;
        // Critical ignores snooze, so its card stays as it is.
        const snoozed = state.cards.find(
          card => card.signal_firing_id === action.payload
        );
        if (snoozed && snoozed.tier !== "critical")
          state.cards = advanceQueue(state.cards, action.payload);
      })
      .addCase(snoozeSignal.rejected, state => {
        state.actionInFlightId = null;
      })
      .addCase(dismissSignal.pending, (state, action) => {
        state.actionInFlightId = action.meta.arg;
      })
      .addCase(dismissSignal.fulfilled, (state, action) => {
        state.actionInFlightId = null;
        state.cards = advanceQueue(state.cards, action.payload);
      })
      .addCase(dismissSignal.rejected, state => {
        state.actionInFlightId = null;
      });
  },
});

export const { clearSignalFeed } = signalFeedSlice.actions;
export default signalFeedSlice.reducer;
